import Input from "Components/Form/Input";
import { FormEvent, useState } from "react";
import moment, { Moment } from "moment";
import { createToast } from "Utils/toastFunc";
import { Button } from "Components/Button";
import InputDateRange from "Components/Form/InputDateRange";
import Select from "Components/Form/Select";
import SelectSearch from "Components/Form/SelectSearch";
import { useIsFetching, useQuery } from "react-query";
import { Box } from "styles/globalComponents";
import { getUnitsByUser, Unit } from "Requests/PreOrder";
import { preBudgetCombos } from "Requests/Partners/PreBudgets";
import {
  FiltersState,
  initialPrePreOrderFilters,
} from "../usePrePreOrderListUnit";
import { FiltersStyled, FormContainer } from "../styles";

moment.locale("pt-br");

interface PrePreOrderFiltersProps {
  setFilters: (filters: FiltersState) => void;
  itemsQuantity: number;
  setNewPage: (page: number) => void;
}

const SEARCH_TYPE_OPTIONS = [
  { id: "", name: "Tipo de busca" },
  { id: "name", name: "Nome" },
  { id: "email", name: "Email" },
  { id: "phone", name: "Telefone" },
  { id: "document", name: "Documento" },
];

const PRE_ORDER_TYPES = [
  { id: "particular", name: "Particular" },
  { id: "convenio", name: "Convênio" },
];

type LocalFilters = {
  searchTypeOption: typeof SEARCH_TYPE_OPTIONS[number];
  searchValue: string;
  start_date: Moment | undefined;
  end_date: Moment | undefined;
  unit: string;
  preOrderType: string;
  status_id: number[];
};

const initialLocalFilters: LocalFilters = {
  searchTypeOption: SEARCH_TYPE_OPTIONS[0],
  searchValue: "",
  start_date: undefined,
  end_date: undefined,
  unit: "",
  preOrderType: "",
  status_id: [],
};

export const PrePreOrderFilters = ({
  setFilters,
  itemsQuantity,
  setNewPage,
}: PrePreOrderFiltersProps) => {
  const [filters, setLocalFilters] =
    useState<LocalFilters>(initialLocalFilters);

  const units = useQuery<Unit[]>({
    queryFn: getUnitsByUser,
    queryKey: ["user-units"],
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const combos = useQuery({
    queryFn: preBudgetCombos,
    queryKey: ["pre-order-combo"],
    refetchOnWindowFocus: false,
  });

  const statusOptions =
    combos.data?.status_pre_orders?.map(
      (s: { id: number; status: string }) => ({
        id: s.id,
        name: s.status,
      })
    ) ?? [];

  const isFetching =
    useIsFetching({
      queryKey: ["user-units"],
    }) > 0;

  function hasAnyFilterSelected(): boolean {
    const hasSearchPair =
      Boolean(filters.searchTypeOption.id) &&
      filters.searchValue.trim().length > 0;

    const hasOther =
      Boolean(filters.start_date) ||
      Boolean(filters.end_date) ||
      Boolean(filters.unit) ||
      Boolean(filters.preOrderType) ||
      filters.status_id.length > 0;

    return hasSearchPair || hasOther;
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const hasType = Boolean(filters.searchTypeOption.id);
    const hasValue = filters.searchValue.trim().length > 0;

    if (hasType && !hasValue) {
      createToast(
        "error",
        "Você selecionou um tipo de busca, mas não informou o valor"
      );
      return;
    }

    if (!hasType && hasValue) {
      createToast("error", "Selecione o tipo de busca");
      return;
    }

    if (!hasAnyFilterSelected()) {
      createToast("error", "Selecione ao menos um filtro");
      return;
    }

    setNewPage(1);

    const updatedFilters: FiltersState = {
      searchType: filters.searchTypeOption.id,
      searchValue: filters.searchValue.trim(),
      start_date: filters.start_date,
      end_date: filters.end_date,
      unit: filters.unit,
      preOrderType: filters.preOrderType,
      status_id: filters.status_id,
    };

    setFilters(updatedFilters);
  }

  function clearFilters() {
    setFilters(initialPrePreOrderFilters);
    setLocalFilters(initialLocalFilters);
  }

  function handleDatesChange(value: any) {
    if (value.startDate) {
      setLocalFilters((prev) => ({
        ...prev,
        start_date: value.startDate,
      }));
    }

    if (value.endDate) {
      setLocalFilters((prev) => ({
        ...prev,
        end_date: value.endDate,
      }));
    }
  }

  return (
    <FiltersStyled className="filters-holder">
      <div className="filters-order">
        <FormContainer className="select-search-submit" onSubmit={handleSubmit}>
          <div className="select-search-submit-types">
            <SelectSearch
              id="pre-preorder-search-type"
              label=""
              value={filters.searchTypeOption}
              error=""
              isMulti
              isMoreSelect={false}
              options={SEARCH_TYPE_OPTIONS}
              loading={false}
              readOnly={false}
              valueKey="id"
              labelKey="name"
              placeholder="Tipo de busca"
              onChange={(selected: typeof SEARCH_TYPE_OPTIONS[number]) => {
                if (!selected) {
                  return;
                }
                if (selected.id === "") {
                  setLocalFilters((prev) => ({ ...prev, searchValue: "" }));
                }
                setLocalFilters((prev) => ({
                  ...prev,
                  searchTypeOption: selected,
                }));
              }}
            />
          </div>

          <Input
            readOnly={!filters.searchTypeOption.id}
            placeholder="Buscar por nome, CPF, e-mail ou telefone"
            value={filters.searchValue}
            onChange={({ target }: any) =>
              setLocalFilters((prev) => ({
                ...prev,
                searchValue: target.value,
              }))
            }
            type="text"
            error=""
            id="search-value-pre-preorder"
            description=""
            label=""
            mask=""
            maskChar=""
            maxLength={200}
            min={1}
            multiline={false}
            onBlur={() => {}}
            onFocus={() => {}}
            onKeyPress={() => {}}
            purple={false}
            required={false}
            small={false}
          />

          <div className="date-range">
            <InputDateRange
              startDatePlaceholderText="Data inicial"
              endDatePlaceholderText="Data final"
              startDateId="start-pre-preorder"
              endDateId="end-pre-preorder"
              onDatesChange={(value: any) => handleDatesChange(value)}
              openInModal={false}
              dates={{
                startDate: filters.start_date,
                endDate: filters.end_date,
              }}
              blockPastChanges={false}
              withClear={false}
              error={undefined}
              small={undefined}
              purple={undefined}
              label={undefined}
            />
          </div>

          <div className="state ">
            <Select
              id="unit"
              placeholder="Selecione a unidade"
              optionName="name"
              options={units.data}
              readOnly={false}
              value={filters.unit}
              onChange={({ target: { value } }: any) =>
                setLocalFilters((prev) => ({ ...prev, unit: value }))
              }
            />
          </div>

          <div className="state ">
            <Select
              id="preOrderType"
              placeholder="Tipo de pré-pedido"
              optionName="name"
              options={PRE_ORDER_TYPES}
              readOnly={false}
              value={filters.preOrderType}
              onChange={({ target: { value } }: any) =>
                setLocalFilters((prev) => ({ ...prev, preOrderType: value }))
              }
            />
          </div>

          <div className="state multiples">
            <SelectSearch
              id="status_id"
              label=""
              value={statusOptions.filter((option) =>
                filters.status_id.includes(option.id)
              )}
              error=""
              isMulti
              isMoreSelect
              options={statusOptions}
              loading={!statusOptions.length}
              readOnly={!statusOptions.length}
              valueKey="id"
              labelKey="name"
              placeholder="Status"
              onChange={(values: { id: number; name: string }[] | null) => {
                const statusIds = (values || []).map((value) => value.id);
                setLocalFilters((prev) => ({
                  ...prev,
                  status_id: statusIds,
                }));
              }}
            />
          </div>

          <Button onClick={handleSubmit} type="submit">
            Buscar
          </Button>
          <Button onClick={clearFilters} textButton>
            Limpar busca
          </Button>
        </FormContainer>

        {isFetching ? null : (
          <Box mt="20" display="flex" justify="end">
            <p>{itemsQuantity} resultado(s) encontrado(s)</p>
          </Box>
        )}
      </div>
    </FiltersStyled>
  );
};
